import { notification } from "antd";
import { all, call, put, takeLatest } from "redux-saga/effects";
import {
  generatePqrSuccess,
  generatePqrFailure,
  getPQRReportsSuccess,
  getPQRReportsFailure,
} from "./action.js";
import {
  FETCH_PQR_REPORTS_REQUEST,
  GENERATE_PQR_REQUEST,
  GET_REPORTS_INFO_REQUEST,
  EXCLUDED_TLOG_SAGA,
  PQR_LOADER_START,
  PQR_LOADER_STOP,
  SET_REPORTS_INFO,
} from "./types.js";
import { ADMIN_URL } from "../../config.js";
import fetch from "../../services/fetch.js";

function* generatePqrSaga(action) {
  try {
    yield put({ type: PQR_LOADER_START });

    const {
      projectId,
      weldNumbers,
      email_address,
      boundary,
      columns_add,
      items,
      excluded_tlogs,
    } = action.payload;

    const url = `${ADMIN_URL}/v1/pqr/generate-pqr`;

    const res = yield fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        projectId,
        weldNumbers : weldNumbers?.join(","),
        email_address,
        boundary,
        columns_add,
        items,
        excluded_tlogs,
      }),
    });

    if (res.status === 403) {
      yield put({ type: PQR_LOADER_STOP });
      throw new Error("Unauthorized");
    }

    const response = yield res.json();

    if (res.status === 200 || res.status === 202) {
      if (response.data) {
        yield put(generatePqrSuccess(response.data?.data));
        yield put({ type: PQR_LOADER_STOP });
        notification.success({
          message:
            response.data?.message || "PQR generation initiated successfully",
          duration: 4,
        });
        if (typeof action.cb === "function") {
          action.cb(response.data?.data);
        }
        return;
      }
    }

    yield put(generatePqrFailure(response.message || "Failed to generate PQR"));
    yield put({ type: PQR_LOADER_STOP });

    if (response.status === 400 || response.status === 410) {
      notification.warn({
        message: response.message || "Invalid request parameters",
        duration: 6,
      });
      if (typeof action.error === "function") {
        action.error(response.message);
      }
    } else {
      notification.error({
        message: response.message || "Failed to generate PQR",
      });
      if (typeof action.error === "function") {
        action.error(response.message);
      }
    }
  } catch (error) {
    console.error("Error in generatePqrSaga:", error);
    yield put(generatePqrFailure(error.message));
    yield put({ type: PQR_LOADER_STOP });
    notification.error({
      message: error.message || "Failed to generate PQR",
    });
    if (typeof action.error === "function") {
      action.error(response.message);
    }
  }
}

function* getReportsInfoSaga(action) {
  try {
    yield put({ type: PQR_LOADER_START });

    const { project_id, weld_numbers, boundary } = action.payload;
    const weldNumbersParam = Array.isArray(weld_numbers)
      ? weld_numbers.join(",")
      : weld_numbers;

    const url = `${ADMIN_URL}/v1/pqr/reports-info?project_id=${project_id}&weld_numbers=${weldNumbersParam}&boundary=${boundary}`;

    const res = yield fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    const response = yield res.json();

    if (res.ok) {
      // Extract the data consistently
      const responseData = response.data || response;
      yield put({
        type: SET_REPORTS_INFO,
        payload: { data: responseData, error: null },
      });
      if (typeof action.payload.cb === "function") {
        action.payload.cb(responseData);
      }
      if (typeof action.cd === "function") {
        action.cd(responseData);
      }
      if (typeof action.cb === "function") {
        action.cb(response?.data);
      }
      if (typeof action.payload === "function") {
        action.payload(responseData);
      }
    } else {
      const errorMessage = response.message || "Request failed";
      yield put({
        type: SET_REPORTS_INFO,
        payload: { data: null, error: errorMessage },
      });
      notification.error({ message: errorMessage });
      if (typeof action.payload.error === "function") {
        action.payload.error(errorMessage);
      }
    }
  } catch (error) {
    const errorMessage = error.message || "Network error";
    yield put({
      type: SET_REPORTS_INFO,
      payload: { data: null, error: errorMessage },
    });
    notification.error({ message: errorMessage });
    if (typeof action.payload.error === "function") {
      action.payload.error(errorMessage);
    }
  } finally {
    yield put({ type: PQR_LOADER_STOP });
  }
}

function* fetchPQRReportsSaga(action) {
  try {
    yield put({ type: PQR_LOADER_START });
    const { project_id } = action.payload;

    if (!project_id) {
      throw new Error("Project ID is required");
    }
    const url = `${ADMIN_URL}/v1/pqr/reports-with-pass-view?project_id=${project_id}`;

    const res = yield call(fetch, url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (!res.ok) {
      const errorResponse = yield res.json();
      throw new Error(errorResponse.message || "Failed to fetch PQR reports");
    }
    const response = yield res.json();
    const responseData = response.data || response;
    yield put(getPQRReportsSuccess(responseData));
  } catch (error) {
    console.error("Error in fetchPQRReportsSaga:", error);
    yield put(getPQRReportsFailure(error.message));
    notification.error({
      message: error.message || "Failed to fetch PQR reports",
    });
  } finally {
    yield put({ type: PQR_LOADER_STOP });
  }
}

function* exlcudeTlogSaga(action) {
  try {
    const { project_id, weld_numbers, boundary, excluded_tlogs } =
      action.payload;

    if (!project_id) {
      yield put({ type: PQR_LOADER_START });

      return;
    }
    const url = `${ADMIN_URL}/v1/pqr/exclude-tlogs`;

    yield put({ type: PQR_LOADER_START });

    // Create the body object
    const body = {
      ...(project_id && { project_id }),
      ...(weld_numbers && { weld_numbers }),
      ...(boundary && { boundary }),
      ...(excluded_tlogs && { excluded_tlogs }),
    };

    const res = yield fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (res.statusCode === 403) {
      throw new Error("Unauthorized");
    }

    const response = yield res.json();
    console.log("responsesaga", response);

    switch (response.statusCode) {
      case 200:
        console.log("action", action);
        console.log("action.cd", action.cd);
        if (typeof action.cb === "function") {
          action.cb(response?.data);
        }
        break;
      case 410:
        notification.warn({
          message: response?.message,
          duration: 6,
        });
        break;
      default:
        notification.error({
          message: "Something went wrong.",
        });
    }

    yield put({ type: PQR_LOADER_STOP });
  } catch (error) {
    notification.error({
      message: error.message || "Failed to fetch slogs.",
    });
    if (typeof action.error === "function") {
      action.error();
    }
    yield put({ type: PQR_LOADER_STOP });
  }
}

function* pqrWatcher() {
  yield takeLatest(GENERATE_PQR_REQUEST, generatePqrSaga);
  yield takeLatest(GET_REPORTS_INFO_REQUEST, getReportsInfoSaga);
  yield takeLatest(FETCH_PQR_REPORTS_REQUEST, fetchPQRReportsSaga);
  yield takeLatest(EXCLUDED_TLOG_SAGA, exlcudeTlogSaga);
}

export default function* pqrSagas() {
  yield all([call(pqrWatcher)]);
}
